/**
 * TemplateGallery — Feb 2026 R138d
 *
 * Dedicated HR screen to preview, clone, edit, and delete onboarding /
 * offboarding templates side-by-side. Left pane: list of templates
 * (grouped by kind). Right pane: preview of the selected template's
 * tasks with inline edit + delete.
 *
 * Route: /app/hr/templates
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LayoutGrid, Copy, Trash2, Pencil, Eye, EyeOff, Sparkles, LogOut, Plus, Check, X, ArrowUp, ArrowDown, ListPlus } from "lucide-react";

const OWNER_ROLES = ["hr", "manager", "buddy", "it", "finance", "admin", "employee"];
const ONB_CATS = ["pre_boarding", "day_0", "day_7", "day_30", "day_60", "day_90"];
const OFF_CATS = ["notice", "handover", "clearance", "farewell", "final_pay", "alumni"];

export default function TemplateGallery() {
  const { activeWorkspaceId } = useAuth();
  const [kind, setKind] = useState("onboarding");
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState("");

  const url = kind === "onboarding"
    ? `/workspaces/${activeWorkspaceId}/onboarding/templates`
    : `/workspaces/${activeWorkspaceId}/offboarding/templates`;

  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const r = await api.get(url);
      setTemplates(r.data || []);
      if ((r.data || []).length && !selectedId) setSelectedId(r.data[0].id);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load templates")); }
  };
  useEffect(() => { setSelectedId(null); load(); /* eslint-disable-next-line */ }, [kind, activeWorkspaceId]);

  const selected = useMemo(() => templates.find((t) => t.id === selectedId), [templates, selectedId]);

  const clone = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/${kind}/templates/${selected.id}/clone`,
        { name: cloneName.trim() || undefined });
      toast.success(`Cloned as “${r.data.name}”`);
      setCloneOpen(false); setCloneName("");
      await load();
      setSelectedId(r.data.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete “${selected.name}”? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/${kind}/templates/${selected.id}`);
      toast.success("Template deleted");
      setSelectedId(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const persistTasks = async (nextTasks) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/${kind === "onboarding" ? "onboarding" : "offboarding"}/templates/${selected.id}`,
        { tasks: nextTasks });
      toast.success("Template updated");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        icon={LayoutGrid}
        eyebrow="HR TEMPLATES"
        title="Onboarding & Offboarding Templates"
        description="Preview, clone, edit and delete the templates HR uses to seed every new hire and exit checklist."
      />
      <div className="px-4 sm:px-6 md:px-10 py-4">
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList className="rounded-none">
            <TabsTrigger value="onboarding" className="rounded-none" data-testid="tpl-tab-onboarding">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Onboarding
            </TabsTrigger>
            <TabsTrigger value="offboarding" className="rounded-none" data-testid="tpl-tab-offboarding">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Offboarding
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 mt-4">
          {/* Left · list */}
          <div className="border border-border" data-testid="tpl-list">
            <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center justify-between">
              <span>{templates.length} template{templates.length === 1 ? "" : "s"}</span>
            </div>
            {templates.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground italic">No templates yet — save one from any in-flight journey to see it here.</div>
            ) : templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/20 ${selectedId === t.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
                data-testid={`tpl-row-${t.id}`}
              >
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {t.name}
                  {t.is_default && <span className="text-[9px] uppercase tracking-widest bg-emerald-500/10 text-emerald-700 px-1 border border-emerald-500/30">default</span>}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {(t.tasks || []).length} tasks · scope: {t.scope || "workspace"}
                  {t.cloned_from_journey_id && " · from journey"}
                  {t.cloned_from_id && " · cloned"}
                </div>
              </button>
            ))}
          </div>

          {/* Right · preview + edit */}
          <div className="border border-border" data-testid="tpl-preview">
            {!selected ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Select a template on the left to preview.</div>
            ) : (
              <TemplateEditor
                kind={kind}
                template={selected}
                busy={busy}
                onClone={() => { setCloneName(`${selected.name} (copy)`); setCloneOpen(true); }}
                onDelete={remove}
                onPersistTasks={persistTasks}
              />
            )}
          </div>
        </div>
      </div>

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="max-w-md" data-testid="tpl-clone-dialog">
          <DialogHeader><DialogTitle>Clone template</DialogTitle></DialogHeader>
          <Input value={cloneName} onChange={(e) => setCloneName(e.target.value)} className="rounded-none h-9" placeholder="New template name" data-testid="tpl-clone-name" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setCloneOpen(false)}>Cancel</Button>
            <Button className="rounded-none" disabled={busy} onClick={clone} data-testid="tpl-clone-save">Clone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateEditor({ kind, template, busy, onClone, onDelete, onPersistTasks }) {
  const [tasks, setTasks] = useState(template.tasks || []);
  const [dirty, setDirty] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => { setTasks(template.tasks || []); setDirty(false); }, [template]);

  const cats = kind === "onboarding" ? ONB_CATS : OFF_CATS;
  const updateTask = (idx, patch) => {
    setTasks((ts) => ts.map((t, i) => i === idx ? { ...t, ...patch } : t));
    setDirty(true);
  };
  const removeTask = (idx) => {
    setTasks((ts) => ts.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const addTask = (t) => {
    setTasks((ts) => [...ts, t]);
    setDirty(true);
    setAddOpen(false);
  };
  const addTasksBulk = (list) => {
    if (!list || list.length === 0) return;
    setTasks((ts) => [...ts, ...list]);
    setDirty(true);
    setBulkOpen(false);
  };
  // Feb 2026 — Reorder within a section only. Swap the task at `idx`
  // with its neighbour in the SAME category. Keeps the flat array
  // shape the backend expects while giving PMs sequence control.
  const moveWithinSection = (idx, dir) => {
    setTasks((ts) => {
      const t = ts[idx];
      if (!t) return ts;
      const cat = t.category || cats[0];
      // Find neighbour in same category.
      const step = dir === "up" ? -1 : 1;
      let j = idx + step;
      while (j >= 0 && j < ts.length) {
        if ((ts[j].category || cats[0]) === cat) break;
        j += step;
      }
      if (j < 0 || j >= ts.length) return ts;
      const next = ts.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const save = () => onPersistTasks(tasks);

  // Group tasks by category, preserving original order within each group.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const c of cats) m.set(c, []);
    tasks.forEach((t, i) => {
      const c = t.category || cats[0];
      if (!m.has(c)) m.set(c, []);
      m.get(c).push({ t, i });
    });
    return [...m.entries()];
  }, [tasks, cats]);

  return (
    <div>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{kind} template</div>
          <div className="text-lg font-bold" data-testid="tpl-preview-title">{template.name}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={onClone} disabled={busy} data-testid="tpl-clone-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Clone
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8 text-rose-600 hover:bg-rose-50" onClick={onDelete} disabled={busy || template.is_default} title={template.is_default ? "Cannot delete the default" : ""} data-testid="tpl-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
          {dirty && (
            <Button size="sm" className="rounded-none h-8" onClick={save} disabled={busy} data-testid="tpl-save-btn">
              <Check className="w-3.5 h-3.5 mr-1" /> Save changes
            </Button>
          )}
        </div>
      </div>
      <div className="p-3 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{tasks.length} tasks</div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setBulkOpen(true)} data-testid="tpl-bulk-add-btn">
              <ListPlus className="w-3 h-3 mr-1" /> Bulk add
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setAddOpen(true)} data-testid="tpl-add-task-btn">
              <Plus className="w-3 h-3 mr-1" /> Add task
            </Button>
          </div>
        </div>
        {tasks.length === 0 && (
          <div className="border border-dashed border-border p-4 text-xs text-center text-muted-foreground italic">No tasks yet</div>
        )}
        {grouped.map(([cat, entries]) => {
          if (entries.length === 0) return null;
          return (
            <div key={cat} className="border border-border" data-testid={`tpl-section-${cat}`}>
              <div className="px-2 py-1 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center justify-between">
                <span>{cat.replace(/_/g, " ")}</span>
                <span className="font-mono normal-case">{entries.length}</span>
              </div>
              <div className="divide-y divide-border/60">
                {entries.map(({ t, i }, position) => (
                  <TemplateTaskRow
                    key={`${t.key || t.title}-${i}`}
                    task={t}
                    cats={cats}
                    onChange={(patch) => updateTask(i, patch)}
                    onRemove={() => removeTask(i)}
                    onMoveUp={position > 0 ? () => moveWithinSection(i, "up") : null}
                    onMoveDown={position < entries.length - 1 ? () => moveWithinSection(i, "down") : null}
                    testid={`tpl-task-${i}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {addOpen && (
        <TemplateNewTaskDialog cats={cats} onAdd={addTask} onClose={() => setAddOpen(false)} />
      )}
      {bulkOpen && (
        <TemplateBulkAddDialog cats={cats} onAdd={addTasksBulk} onClose={() => setBulkOpen(false)} />
      )}
    </div>
  );
}

function TemplateTaskRow({ task, cats, onChange, onRemove, onMoveUp, onMoveDown, testid }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/10">
        <div className="flex flex-col shrink-0">
          <button className={`w-4 h-3 flex items-center justify-center ${onMoveUp ? "text-muted-foreground hover:text-primary" : "text-muted-foreground/30 cursor-not-allowed"}`}
                  onClick={onMoveUp || undefined}
                  disabled={!onMoveUp}
                  title="Move up within section"
                  data-testid={`${testid}-up`}>
            <ArrowUp className="w-3 h-3" />
          </button>
          <button className={`w-4 h-3 flex items-center justify-center ${onMoveDown ? "text-muted-foreground hover:text-primary" : "text-muted-foreground/30 cursor-not-allowed"}`}
                  onClick={onMoveDown || undefined}
                  disabled={!onMoveDown}
                  title="Move down within section"
                  data-testid={`${testid}-down`}>
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
        <button className="text-muted-foreground hover:text-primary" onClick={() => setExpanded((v) => !v)}>
          <Pencil className="w-3 h-3" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{task.title || <em className="text-muted-foreground">(untitled)</em>}</div>
          {task.description && <div className="text-[11px] text-muted-foreground truncate">{task.description}</div>}
        </div>
        <span className="text-[10px] border border-border px-1.5 py-0.5 capitalize">{task.owner_role || "hr"}</span>
        <button
          className={`w-6 h-6 flex items-center justify-center ${task.is_visible_to_employee ? "text-sky-600" : "text-muted-foreground/60"} hover:bg-muted/40`}
          onClick={() => onChange({ is_visible_to_employee: !task.is_visible_to_employee })}
          title={task.is_visible_to_employee ? "Visible to employee" : "Hidden"}
          data-testid={`${testid}-visible`}
        >
          {task.is_visible_to_employee ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
        </button>
        <button className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-rose-600" onClick={onRemove} data-testid={`${testid}-remove`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {expanded && (
        <div className="grid grid-cols-2 gap-2 p-2 border-t border-border bg-muted/30">
          <div className="col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Title</label>
            <Input value={task.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="rounded-none h-8 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Description</label>
            <Textarea value={task.description || ""} onChange={(e) => onChange({ description: e.target.value })} rows={2} className="rounded-none text-sm resize-none" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Owner role</label>
            <Select value={task.owner_role || "hr"} onValueChange={(v) => onChange({ owner_role: v })}>
              <SelectTrigger className="rounded-none h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Category</label>
            <Select value={task.category || cats[0]} onValueChange={(v) => onChange({ category: v })}>
              <SelectTrigger className="rounded-none h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c} value={c} className="text-xs">{c.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Offset days</label>
            <Input type="number" value={task.offset_days ?? 0} onChange={(e) => onChange({ offset_days: parseInt(e.target.value, 10) || 0 })} className="rounded-none h-8 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer pt-4">
            <input type="checkbox" checked={!!task.is_required} onChange={(e) => onChange({ is_required: e.target.checked })} />
            Required
          </label>
        </div>
      )}
    </div>
  );
}

function TemplateNewTaskDialog({ cats, onAdd, onClose }) {
  const [t, setT] = useState({ title: "", description: "", owner_role: "hr", category: cats[0], offset_days: 0, is_required: false, is_visible_to_employee: false });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="tpl-new-task-dialog">
        <DialogHeader><DialogTitle>Add task to template</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Input placeholder="Title" value={t.title} onChange={(e) => setT((s) => ({ ...s, title: e.target.value }))} className="rounded-none h-9" autoFocus data-testid="tpl-new-task-title" />
          <Textarea placeholder="Description" value={t.description} onChange={(e) => setT((s) => ({ ...s, description: e.target.value }))} rows={2} className="rounded-none resize-none" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={t.owner_role} onValueChange={(v) => setT((s) => ({ ...s, owner_role: v }))}>
              <SelectTrigger className="rounded-none h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={t.category} onValueChange={(v) => setT((s) => ({ ...s, category: v }))}>
              <SelectTrigger className="rounded-none h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={t.is_visible_to_employee} onChange={(e) => setT((s) => ({ ...s, is_visible_to_employee: e.target.checked }))} />
            Visible to employee
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}><X className="w-4 h-4 mr-1" /> Cancel</Button>
          <Button className="rounded-none" disabled={!t.title.trim()} onClick={() => onAdd({ ...t, key: `tpl_${Date.now().toString(36)}` })} data-testid="tpl-new-task-add">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Feb 2026 — Bulk task creation. Paste multiple titles (one per line)
// and pick shared owner_role / category / offset_days once. Faster
// than opening the "Add task" dialog 20 times when seeding a fresh
// section.
function TemplateBulkAddDialog({ cats, onAdd, onClose }) {
  const [rawTitles, setRawTitles] = useState("");
  const [shared, setShared] = useState({
    owner_role: "hr",
    category: cats[0],
    offset_days: 0,
    is_required: false,
    is_visible_to_employee: false,
  });
  const lines = useMemo(() => rawTitles.split("\n").map((l) => l.trim()).filter(Boolean), [rawTitles]);
  const handleAdd = () => {
    if (lines.length === 0) return;
    const list = lines.map((title, idx) => ({
      ...shared,
      title,
      description: "",
      key: `tpl_${Date.now().toString(36)}_${idx}`,
    }));
    onAdd(list);
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="tpl-bulk-add-dialog">
        <DialogHeader>
          <DialogTitle>Bulk add tasks</DialogTitle>
          <p className="text-xs text-muted-foreground">
            One title per line. Every task in this batch shares the values below.
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Titles</label>
            <Textarea
              value={rawTitles}
              onChange={(e) => setRawTitles(e.target.value)}
              placeholder={"Set up laptop\nCreate email account\nAdd to team Slack"}
              rows={7}
              className="rounded-none text-sm resize-none font-mono"
              autoFocus
              data-testid="tpl-bulk-add-titles"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              {lines.length} task{lines.length === 1 ? "" : "s"} detected
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Owner role</label>
              <Select value={shared.owner_role} onValueChange={(v) => setShared((s) => ({ ...s, owner_role: v }))}>
                <SelectTrigger className="rounded-none h-9" data-testid="tpl-bulk-add-owner"><SelectValue /></SelectTrigger>
                <SelectContent>{OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Section</label>
              <Select value={shared.category} onValueChange={(v) => setShared((s) => ({ ...s, category: v }))}>
                <SelectTrigger className="rounded-none h-9" data-testid="tpl-bulk-add-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-muted-foreground">Offset days</label>
              <Input type="number" value={shared.offset_days} onChange={(e) => setShared((s) => ({ ...s, offset_days: parseInt(e.target.value, 10) || 0 }))} className="rounded-none h-9 text-sm" data-testid="tpl-bulk-add-offset" />
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={shared.is_required} onChange={(e) => setShared((s) => ({ ...s, is_required: e.target.checked }))} data-testid="tpl-bulk-add-required" />
              Required
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={shared.is_visible_to_employee} onChange={(e) => setShared((s) => ({ ...s, is_visible_to_employee: e.target.checked }))} data-testid="tpl-bulk-add-visible" />
              Visible to employee
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}><X className="w-4 h-4 mr-1" /> Cancel</Button>
          <Button className="rounded-none" disabled={lines.length === 0} onClick={handleAdd} data-testid="tpl-bulk-add-submit">
            Add {lines.length || ""} task{lines.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
